import fetchAction from '../../../actions/fetch';
import { entitlementsApi } from '../../../services/entitlementsApi';
import { ERROR_DEBUG_MESSAGES } from '../constants/paymentErrors';
import { FULFILLMENT_IN_PROGRESS_KEY, FULFILLMENT_GUARD_TTL_MS } from '../constants/fulfillmentGuard';
import { channels, splitsList } from '../../../constants';
import { PAYMENT_LOG_STEPS } from '../../../constants/paymentLogSteps';
import { trackUpsellStage } from '../../../helpers/trackUpsell/trackUpsellStage';
import isFlexUser from '../../../helpers/isFlexUser';
import BILLING_PROVIDERS from '../../../constants/billingProviders';
import { ENTITLEMENTS_PLAN } from '../../../constants/endpoints';

const PURCHASE_ENDPOINT = '/billingsubscriptions/account';
const THREE_DS_REQUIRED_STATUS_CODE = 422;
const MIGRATION_BLOCKED_STATUS_CODE = 400;
const MIGRATION_FALLBACK_ERROR_INFOS = new Set(['MigrationBlocked', 'MigrationFailed']);
const defaultChannel = channels.online;

const getPaymentAuthorizationToken = errorResponseBody =>
  errorResponseBody?.status === THREE_DS_REQUIRED_STATUS_CODE
    ? errorResponseBody.errorResponse?.PaymentAuthorizationRequestToken
    : null;

const getIs3dsInitiatedOnEnforcedVerification = errorResponseBody =>
  errorResponseBody?.status === THREE_DS_REQUIRED_STATUS_CODE
    ? errorResponseBody?.errorResponse?.Is3dsInitiatedOnEnforcedVerification
    : false;

const getIsMigrationFallbackError = errorResponseBody =>
  errorResponseBody?.status === MIGRATION_BLOCKED_STATUS_CODE &&
  MIGRATION_FALLBACK_ERROR_INFOS.has(errorResponseBody?.errorResponse?.ErrorInfo);

const checkDuplicateGuard = (state, targetOfferId, shouldUseLegacyPurchaseFlow, flexUserMigrationFailed) => {
  const flagTimestamp = sessionStorage.getItem(FULFILLMENT_IN_PROGRESS_KEY);
  if (!flagTimestamp) return null;

  const elapsed = Date.now() - parseInt(flagTimestamp, 10);
  if (elapsed >= FULFILLMENT_GUARD_TTL_MS) {
    sessionStorage.removeItem(FULFILLMENT_IN_PROGRESS_KEY);
    return null;
  }

  if (process.env.NODE_ENV !== 'test') {
    trackUpsellStage(
      PAYMENT_LOG_STEPS.DUPLICATE_PAYMENT_GUARD_BLOCKED_AT_PURCHASE,
      state.auth?.currentUser?.customerId,
      state.upsellProgress?.upsellCorrelationId,
      targetOfferId,
      state.selection?.family,
      state.billing?.billingProvider === BILLING_PROVIDERS.NEW
    );
  }

  return {
    complete: false,
    error: ERROR_DEBUG_MESSAGES.PAYMENT.SERVICE_UNAVAILABLE,
    threeDSecureActionTokenId: null,
    is3dsInitiatedOnEnforcedVerification: false,
    requiresFulfillment: shouldUseLegacyPurchaseFlow,
    ...(flexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
  };
};

const handleMigrationFallback = async (dispatch, legacyRequestBody) => {
  try {
    await dispatch(fetchAction(PURCHASE_ENDPOINT, legacyRequestBody));
    return {
      success: true,
      result: {
        complete: true,
        error: null,
        threeDSecureActionTokenId: null,
        is3dsInitiatedOnEnforcedVerification: false,
        requiresFulfillment: true,
        flexUserMigrationFailed: true,
      },
    };
  } catch (error_) {
    const errorResponseBody = await error_?.json?.().catch(() => ({}));
    return { success: false, errorResponseBody };
  }
};

const startPurchase =
  ({
    targetOfferId,
    targetOfferPlanCode,
    isFlexFlow,
    paymentMethodToken,
    authorizationToken,
    isAddingPaymentMethod,
    is3dsInitiatedOnEnforcedVerification,
    channel,
    flexUserMigrationFailed = false,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const isReloadGuardEnabled = state.split?.treatments?.[splitsList.preventDuplicatePayment];
    const isFlexSplitEnabled = isFlexUser(state.split);
    const hasCurrentSubscriptionPlanCode = Boolean(state.subscriptions?.current?.planCode);
    const shouldUseFlexFlow =
      isFlexFlow === true || ((isFlexSplitEnabled || hasCurrentSubscriptionPlanCode) && !flexUserMigrationFailed);
    const shouldUseLegacyPurchaseFlow = !shouldUseFlexFlow;

    if (isReloadGuardEnabled) {
      const guardResult = checkDuplicateGuard(
        state,
        targetOfferId,
        shouldUseLegacyPurchaseFlow,
        flexUserMigrationFailed
      );
      if (guardResult) return guardResult;
    }

    const legacyRequestBody = {
      method: 'PUT',
      body: {
        offerId: targetOfferId,
        paymentAuthorizationResultToken: authorizationToken,
        changeCurrentPaymentMethod: false,
        paymentMethodToken,
        isAddingPaymentMethod,
        is3dsInitiatedOnEnforcedVerification: is3dsInitiatedOnEnforcedVerification ?? false,
      },
    };

    if (shouldUseLegacyPurchaseFlow) {
      try {
        await dispatch(fetchAction(PURCHASE_ENDPOINT, legacyRequestBody));
        return {
          complete: true,
          error: null,
          threeDSecureActionTokenId: null,
          is3dsInitiatedOnEnforcedVerification: false,
          requiresFulfillment: true,
          ...(flexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
        };
      } catch (paymentErrorResponse) {
        const errorResponseBody = await paymentErrorResponse?.json?.().catch(() => ({}));
        const paymentAuthorizationToken = getPaymentAuthorizationToken(errorResponseBody);
        const is3dsCheck = getIs3dsInitiatedOnEnforcedVerification(errorResponseBody);
        if (paymentAuthorizationToken) {
          return {
            complete: false,
            error: null,
            threeDSecureActionTokenId: paymentAuthorizationToken,
            is3dsInitiatedOnEnforcedVerification: is3dsCheck,
            requiresFulfillment: true,
            ...(flexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
          };
        }
        return {
          complete: false,
          error: ERROR_DEBUG_MESSAGES.PAYMENT.NON_3DS_ERROR,
          threeDSecureActionTokenId: null,
          is3dsInitiatedOnEnforcedVerification: false,
          requiresFulfillment: true,
          ...(flexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
        };
      }
    }

    // Both rollout phases use the flex update-plan payload; the transport identifies which flow initiated it.
    const flexRequestBody = {
      planCode: targetOfferPlanCode,
      channel: channel ?? defaultChannel,
      paymentAuthorizationResultToken: authorizationToken,
      changeCurrentPaymentMethod: false,
      paymentMethodToken,
      isAddingPaymentMethod,
      is3dsInitiatedOnEnforcedVerification: is3dsInitiatedOnEnforcedVerification ?? false,
    };

    let isFlexUserMigrationFailed = false;
    let shouldRequireFulfillment = false;
    let errorResponseBody;

    // The redesigned phase 2 flow sets isFlexFlow explicitly and uses RTK Query.
    // Phase 1 is inferred from account state and continues through the fetch middleware.
    if (isFlexFlow === true) {
      const result = await dispatch(entitlementsApi.endpoints.updatePlan.initiate(flexRequestBody));

      if (!('error' in result)) {
        return {
          complete: true,
          error: null,
          threeDSecureActionTokenId: null,
          is3dsInitiatedOnEnforcedVerification: false,
          requiresFulfillment: false,
          ...(flexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
        };
      }

      let errorData = {};
      try {
        errorData = JSON.parse(result.error?.data ?? '{}');
      } catch {
        errorData = {};
      }
      errorResponseBody = { status: result.error?.status, errorResponse: errorData?.errorResponse };
    } else {
      try {
        await dispatch(
          fetchAction(ENTITLEMENTS_PLAN, {
            method: 'PUT',
            body: flexRequestBody,
          })
        );
        return {
          complete: true,
          error: null,
          threeDSecureActionTokenId: null,
          is3dsInitiatedOnEnforcedVerification: false,
          requiresFulfillment: false,
        };
      } catch (error_) {
        errorResponseBody = await error_?.json?.().catch(() => ({}));
      }
    }

    if (getIsMigrationFallbackError(errorResponseBody)) {
      isFlexUserMigrationFailed = true;
      const fallback = await handleMigrationFallback(dispatch, legacyRequestBody);
      if (fallback.success) return fallback.result;
      errorResponseBody = fallback.errorResponseBody;
      shouldRequireFulfillment = true;
    }

    const paymentAuthorizationToken = getPaymentAuthorizationToken(errorResponseBody);
    const is3dsCheck = getIs3dsInitiatedOnEnforcedVerification(errorResponseBody);

    if (paymentAuthorizationToken) {
      return {
        complete: false,
        error: null,
        threeDSecureActionTokenId: paymentAuthorizationToken,
        is3dsInitiatedOnEnforcedVerification: is3dsCheck,
        requiresFulfillment: shouldRequireFulfillment,
        ...(isFlexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
      };
    }

    return {
      complete: false,
      error: ERROR_DEBUG_MESSAGES.PAYMENT.NON_3DS_ERROR,
      threeDSecureActionTokenId: null,
      is3dsInitiatedOnEnforcedVerification: false,
      requiresFulfillment: shouldRequireFulfillment,
      ...(isFlexUserMigrationFailed ? { flexUserMigrationFailed: true } : {}),
    };
  };

export default startPurchase;
